import { useQuery } from '@platform/api-client';
import { Button, EmptyState, formatNumber, PageHeader, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { Printer, Tags } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { useLabelPreferences } from './label-preferences.ts';
import { LabelLines } from './LabelLines.tsx';
import { LabelPages } from './LabelPages.tsx';
import {
  copiesOf,
  MAX_STICKERS,
  pagesOf,
  stickersOf,
  totalOf,
  type Copies,
  type LabelFocus,
  type LabelSubject,
} from './labels.ts';
import { LabelSettings } from './LabelSettings.tsx';
import type { StickerLook } from './Sticker.tsx';
import { fitsOn, loadSymbolDrawer, type DrawSymbol } from './symbols.ts';
import { useLabelSubjects } from './useLabelSubjects.ts';
import styles from './Labels.module.css';
import { BackCrumb } from '../BackCrumb.tsx';

const PAGE_RULES = {
  sheet: '@page { size: A4 portrait; margin: 0; }',
  roll: '@page { size: 50mm 30mm; margin: 0; }',
} as const;

export type LabelsPageProps = {
  api: TypedClient;
  productIds: readonly string[];
  focus: LabelFocus | null;
  onBack: () => void;
};

const unprintableOf = (subjects: readonly LabelSubject[], copies: Copies, look: StickerLook): number =>
  subjects.filter((subject) => {
    if ((copies[subject.variantId] ?? 0) === 0) return false;
    const outcome = look.drawSymbol(look.symbology, subject.sku);
    return outcome.kind === 'unsupported' || !fitsOn(outcome.image, look.format, look.symbology);
  }).length;

type WorkbenchProps = { subjects: readonly LabelSubject[]; focus: LabelFocus | null; drawSymbol: DrawSymbol };

const LabelWorkbench = ({ subjects, focus, drawSymbol }: WorkbenchProps) => {
  const { t, locale } = usePageContext();
  const { format, symbology, showPrice } = useLabelPreferences();
  const [overrides, setOverrides] = useState<Copies>({});
  const [startAt, setStartAt] = useState(1);
  const copies = copiesOf(subjects, focus, overrides);
  const total = totalOf(copies);
  const look: StickerLook = { format, symbology, showPrice, drawSymbol };
  const pages = pagesOf(stickersOf(subjects, copies), format, startAt);
  const unprintable = unprintableOf(subjects, copies, look);
  const printable = total > 0 && total <= MAX_STICKERS;

  return (
    <>
      <div className={styles['layout']}>
        <div className={styles['panel']}>
          <LabelSettings startAt={startAt} onStartAtChange={setStartAt} />
          <LabelLines
            subjects={subjects}
            copies={copies}
            onCopiesChange={(variantId, count) => {
              setOverrides((current) => ({ ...current, [variantId]: count }));
            }}
          />
          <div className={styles['summary']}>
            <span role="status">
              {t('labels.summary', {
                stickers: formatNumber(total, locale),
                pages: formatNumber(pages.length, locale),
              })}
            </span>
            <Button
              icon={<Printer size={20} aria-hidden="true" />}
              disabled={!printable}
              onClick={() => {
                window.print();
              }}
            >
              {t('labels.print')}
            </Button>
          </div>
          {total > MAX_STICKERS ? (
            <p className={styles['warning']}>
              {t('labels.tooMany', { max: formatNumber(MAX_STICKERS, locale) })}
            </p>
          ) : null}
          {unprintable > 0 ? (
            <p className={styles['warning']}>{t('labels.unprintable', { count: unprintable })}</p>
          ) : null}
        </div>
        <div className={styles['preview']}>
          <p className={styles['previewCaption']}>{t('labels.preview')}</p>
          {pages.length === 0 ? (
            <p className={styles['previewCaption']}>{t('labels.previewEmpty')}</p>
          ) : (
            <LabelPages pages={pages.slice(0, 1)} look={look} />
          )}
        </div>
      </div>
      {printable
        ? createPortal(
            <div className={styles['printRoot']}>
              <style>{PAGE_RULES[format]}</style>
              <LabelPages pages={pages} look={look} />
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export const LabelsPage = ({ api, productIds, focus, onBack }: LabelsPageProps) => {
  const { t, describeError } = usePageContext();
  const subjects = useLabelSubjects(api, productIds);
  const drawer = useQuery({
    queryKey: ['label-symbols'],
    queryFn: loadSymbolDrawer,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  const header = (
    <PageHeader
      title={t('labels.title')}
      context={t('labels.context')}
      breadcrumbs={[{ label: t('nav.products'), href: '/products' }, { label: t('labels.title') }]}
      renderLink={(crumb) => <BackCrumb label={crumb.label} onBack={onBack} />}
    />
  );

  if (productIds.length === 0)
    return (
      <>
        {header}
        <EmptyState
          icon={<Tags size={24} />}
          title={t('labels.nothing')}
          description={t('labels.nothingHint')}
          action={
            <Button variant="outline" onClick={onBack}>
              {t('labels.chooseProducts')}
            </Button>
          }
        />
      </>
    );
  if (subjects.state === 'failed')
    return (
      <>
        {header}
        <LoadFailed description={describeError(subjects.error)} onRetry={subjects.retry} />
      </>
    );
  if (subjects.state === 'loading' || drawer.data === undefined)
    return (
      <>
        {header}
        <Skeleton height="240px" label={t('labels.loading')} />
      </>
    );
  return (
    <>
      {header}
      <LabelWorkbench subjects={subjects.subjects} focus={focus} drawSymbol={drawer.data} />
    </>
  );
};
